import { QueryClient } from '@tanstack/react-query'

/**
 * Shared singleton QueryClient.
 *
 * Why a module-level singleton (vs per-render via `useState`)?
 * `useWorkspaceStore.switchWorkspace(id)` MUST call
 * `queryClient.removeQueries({ queryKey: ['ws'] })` BEFORE flipping
 * `activeWsId` (architecture §4.3, invariant C-09). The store cannot
 * reach into a per-component React tree to grab a client from context,
 * so it imports this module-level instance directly.
 *
 * `App.tsx` passes this same instance to `QueryClientProvider`, so the
 * Query DevTools and component-tree consumers see the same cache that
 * the store wipes on workspace switch.
 *
 * Defaults follow story AC-10:
 *   - staleTime  30s   (avoid thrashing on fast remounts in tabbed UI)
 *   - gcTime    300s   (survive a 5-min unmount, e.g. user opens admin)
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      gcTime: 300_000,
    },
    mutations: {
      // Mutations are user-initiated (clicks, form submits). Auto-retrying
      // a failed mutation can double-spend money (EL/FAL credits) or create
      // duplicate side effects, so we surface the error and let the user retry.
      retry: 0,
    },
  },
})
